import { Bin } from '../../../../types/bin';
import { Company } from '../../../../types/company';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { translate } from '../../../../types/translation/Translator';
import { Container } from '../../../../types/container';
import { ContainerType } from '../../../../types/container_type';
import { CustomFieldType, CustomFieldUtil } from '../../../../types/custom_field';
import { StockLocation } from '../../../../types/stock_location';
import { ContainerTableSettingsColumn } from '../../../../types/table_settings/container/ContainerTableSettings';
import { TableSettings } from '../../../../types/table_settings/TableSettings';
import { toMap } from '../../../../util/map.util';
import { TableHeader } from '../Table';
import { toFilterString } from '../../../../util/string.util';
import dayjs from 'dayjs';

export const containerTableHeaders = (
  settings: TableSettings<any>,
  company: Company,
  companies: Map<string, Company>,
  stockLocations: Map<string, StockLocation>,
  bins: Map<string, Bin>,
  containers: Map<string, Container>,
  containerTypes: Map<string, ContainerType>,
  locale: string,
) => {
  const wantedColumns = toMap(settings.columns, 'column');

  const columns: TableHeader<Container>[] = [];

  if (company.partner) {
    columns.push({
      key: 'company',
      name: translate({ key: TranslationKey.company }),
      text: (item: Container) => companies.get(item.companyId)?.name || '',
    });
  }

  if (wantedColumns.has(ContainerTableSettingsColumn.identifier)) {
    columns.push({
      key: ContainerTableSettingsColumn.identifier,
      name: translate({ key: ContainerTableSettingsColumn.identifier }),
      text: (item: Container) => item.identifier,
      column: ContainerTableSettingsColumn.identifier,
    });
  }

  if (wantedColumns.has(ContainerTableSettingsColumn.stockLocation)) {
    columns.push({
      key: ContainerTableSettingsColumn.stockLocation,
      name: translate({ key: ContainerTableSettingsColumn.stockLocation }),
      text: (item: Container) => stockLocations.get(item.stockLocationId)?.name || '',
      column: ContainerTableSettingsColumn.stockLocation,
    });
  }

  if (wantedColumns.has(ContainerTableSettingsColumn.bin)) {
    columns.push({
      key: ContainerTableSettingsColumn.bin,
      name: translate({ key: ContainerTableSettingsColumn.bin }),
      text: (item: Container) => bins.get(item.binId)?.name || '',
      column: ContainerTableSettingsColumn.bin,
    });
  }

  if (wantedColumns.has(ContainerTableSettingsColumn.parentContainer)) {
    columns.push({
      key: ContainerTableSettingsColumn.parentContainer,
      name: translate({ key: ContainerTableSettingsColumn.parentContainer }),
      text: (item: Container) => containers.get(item.parentId || '')?.identifier || '',
      column: ContainerTableSettingsColumn.parentContainer,
    });
  }

  if (wantedColumns.has(ContainerTableSettingsColumn.containerType)) {
    columns.push({
      key: ContainerTableSettingsColumn.containerType,
      name: translate({ key: ContainerTableSettingsColumn.containerType }),
      text: (item: Container) => containerTypes.get(item.containerTypeId || '')?.name || '',
      column: ContainerTableSettingsColumn.containerType,
    });
  }

  const defaultKeys = new Set(Object.keys(ContainerTableSettingsColumn));
  const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

  customFieldColumns.forEach(item => {
    columns.push({
      key: item.column,
      name: item.label,
      sortValue: (container: Container) => {
        const customFieldValue = container.customFieldValues().find(i => i.name === item.column);
        if (!customFieldValue) return '';
        if (customFieldValue.type === CustomFieldType.date) return dayjs(customFieldValue.value);
        return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
      },
      text: (container: Container) => {
        const customFieldValue = container.customFieldValues().find(i => i.name === item.column);
        if (!customFieldValue) return '';
        return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
      },
      column: item.column,
    });
  });

  return columns.sort(
    (a: TableHeader<Container>, b: TableHeader<Container>) =>
      (wantedColumns.get(a.column || '')?.index || -1) - (wantedColumns.get(b.column || '')?.index || -1),
  );
};

export const containerTableFilter = (
  item: Container,
  filter: string,
  settings: TableSettings<any>,
  stockLocations: Map<string, StockLocation>,
  bins: Map<string, Bin>,
  containers: Map<string, Container>,
  containerTypes: Map<string, ContainerType>,
) => {
  const filterString = toFilterString(filter);
  const wantedColumns = toMap(settings.columns, 'column');

  if (!filter) return true;

  if (
    wantedColumns.has(ContainerTableSettingsColumn.identifier) &&
    item.identifier.toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(ContainerTableSettingsColumn.stockLocation) &&
    item.stockLocationId &&
    stockLocations.get(item.stockLocationId)?.name.toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(ContainerTableSettingsColumn.bin) &&
    item.binId &&
    bins.get(item.binId)?.name.toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(ContainerTableSettingsColumn.parentContainer) &&
    item.parentId &&
    containers.get(item.parentId)?.identifier.toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(ContainerTableSettingsColumn.containerType) &&
    item.containerTypeId &&
    containerTypes.get(item.containerTypeId)?.name.toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  const defaultKeys = new Set(Object.keys(ContainerTableSettingsColumn));
  const customFieldColumns = [...wantedColumns.values()].filter(c => !defaultKeys.has(c.column));

  for (const custom of customFieldColumns) {
    const field = toFilterString(item.customFieldValues().find(i => i.name === custom.column)?.value);
    if (field?.includes(filterString)) return true;
  }

  return false;
};
